import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { SimpleGrid } from "@chakra-ui/react";
import { useEffect, useState, JSX } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "src/components/ErrorMessage";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { FundMove } from "@/api/FundMove";
import { fundMoveService } from "@/service/FundMoveService";
import { useProjectNames } from "@/hooks/useProjectNames";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import DateSelectController from "@/components/fields/DateSelect";
import BudgetYearController from "@/components/fields/BudgetYearController";
import { AmountInputController } from "@/components/fields/AmountInput";
import { donationsCodeService } from "@/service/DonationCodeService";
import { convert, DonationCodeController, DonationCodeOption } from "@/components/fields/DonationCodeSelect";
import FundSourceController from "@/components/fields/FundSourceController";
import { fundService } from "@/service/FundService";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface FundMoveFormProps {
    fundMoveId?: number;
    organizationId: number;
    onSubmit: (entry: FundMove) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

const FundMoveForm = ({ fundMoveId, organizationId, onSubmit, viewOnly, header }: FundMoveFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization();
    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = useForm<FundMove>({
        defaultValues: {
            id: fundMoveId ?? -1,
            transferDate: new Date(),
            fromProjectId: -1,
            fromDonationCode: "",
            budgetYear: new Date().getFullYear(),
            toProjectId: -1,
            toDonationCode: "",
            transfer: 0,
            fundSourceId: null,
            fundSourceName: "",
            organizationId,
        },
    });

    const [serverError, setServerError] = useState("");
    const { options: projectNames } = useProjectNames();
    const [donationCodes, setDonationCodes] = useState<DonationCodeOption[]>([]);
    const [institutions, setInstitutions] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if ((fundMoveId ?? -1) > -1) {
            fundMoveService.find(fundMoveId!).then(p => reset(p));
        }
    }, [fundMoveId, reset]);

    useEffect(() => {
        const codes = donationsCodeService.listAll().then(r => setDonationCodes(convert(r)));
        const sources = fundService.fundSourceNames().then(setInstitutions);
        Promise.all([codes, sources]);
    }, []);

    const readOnly = viewOnly ?? false;

    const onSubmitInternal = (entry: FundMove): Promise<void> => {
        const payload: FundMove = {
            ...entry,
            organizationId,
            fundSourceId: entry.fundSourceId ?? null,
            fundSourceName: entry.fundSourceName ?? null,
        };
        return onSubmit(payload)
            .then(() => navigate("/funds/moves"))
            .catch(() => setServerError("Fehler"));
    };

    return (
        <FormPage title={header} description={t('forms.fundAllocations.description')} maxW='5xl'
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/funds/moves')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError.length > 0 && <ErrorMessage message={serverError} />}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap='6'>
                <SectionPanel title={t('forms.sections.source')}>
                    <ProjectSelectController control={control} projectNames={projectNames} name="fromProjectId" label="Von Projekt" readOnly={readOnly} required />
                    <DonationCodeController control={control} codes={donationCodes} name="fromDonationCode" label="Von Kostenstelle" readOnly={readOnly} required />
                </SectionPanel>

                <SectionPanel title={t('forms.sections.target')}>
                    <ProjectSelectController control={control} projectNames={projectNames} name="toProjectId" label="Auf Projekt" readOnly={readOnly} required />
                    <DonationCodeController control={control} codes={donationCodes} name="toDonationCode" label="Auf Kostenstelle" readOnly={readOnly} required />
                </SectionPanel>
            </SimpleGrid>

            <SectionPanel title={t('forms.fundAllocations.details')}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap='4'>
                    <DateSelectController label="Datum" control={control} name="transferDate" clearable={false} required disabled={readOnly} />
                    <BudgetYearController control={control} name="budgetYear" label="Fonds Jahr" readOnly={readOnly} required />
                    <AmountInputController control={control} name="transfer" label={`Summe (${organization?.currency ?? '—'})`} readOnly={readOnly} />
                </SimpleGrid>

                <FundSourceController
                    control={control}
                    name="fundSourceName"
                    label="Fonds Quelle (oder neue Institution erstellen)"
                    readOnly={readOnly}
                    options={institutions}
                />

            </SectionPanel>
        </FormPage>
    );
};

export default FundMoveForm;
